import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LOCALE_LABELS, SUPPORTED_LOCALES, setAppLocale, type AppLocale } from '../i18n';

type FooterItem =
  | { kind: 'link'; to: string; label: string }
  | { kind: 'soon'; label: string };

function FooterLinkList({ items, soonLabel }: { items: FooterItem[]; soonLabel: string }) {
  return (
    <ul className="vbb-rich-footer-list">
      {items.map((item) => (
        <li key={item.label}>
          {item.kind === 'link' ? (
            <Link to={item.to}>{item.label}</Link>
          ) : (
            <span className="vbb-rich-footer-soon" title={soonLabel}>
              {item.label}
              <em>{soonLabel}</em>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

export function ProfileRichFooter() {
  const { t, i18n } = useTranslation(['common', 'nav']);
  const locale = (i18n.resolvedLanguage || i18n.language || 'az').split('-')[0];
  const soon = t('common:comingSoon');

  const casinoItems: FooterItem[] = [
    { kind: 'link', to: '/games', label: t('nav:games') },
    { kind: 'soon', label: t('common:footerLiveCasino') },
    { kind: 'link', to: '/providers', label: t('common:footerProviders') },
    { kind: 'soon', label: t('common:footerFastGames') },
  ];

  const sportsItems: FooterItem[] = [
    { kind: 'link', to: '/sports', label: t('common:liveSports') },
    { kind: 'link', to: '/sports', label: t('common:prematch') },
    { kind: 'soon', label: t('common:footerEsports') },
  ];

  const infoItems: FooterItem[] = [
    { kind: 'link', to: '/qaydalar', label: t('common:footerRules') },
    { kind: 'soon', label: t('common:footerTournaments') },
    { kind: 'link', to: '/bonuses', label: t('nav:bonuses') },
    { kind: 'link', to: '/referral', label: t('common:footerPartnership') },
  ];

  const supportItems: FooterItem[] = [
    { kind: 'link', to: '/support', label: t('common:footerTechSupport') },
    { kind: 'link', to: '/security', label: t('common:securitySettings') },
  ];

  function scrollTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <footer className="vbb-rich-footer" aria-label={t('common:footerAria')}>
      <div className="vbb-rich-footer-brand">
        <span className="vbb-pf-footer-logo">
          VBB<span>PLAY</span>
        </span>
        <label className="vbb-pf-footer-language">
          <select
            aria-label={t('common:language')}
            value={locale}
            onChange={(e) => {
              const next = e.target.value as AppLocale;
              if ((SUPPORTED_LOCALES as readonly string[]).includes(next)) setAppLocale(next);
            }}
          >
            {SUPPORTED_LOCALES.map((code) => (
              <option key={code} value={code}>
                {LOCALE_LABELS[code]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="vbb-rich-footer-cols">
        <section>
          <h2>{t('nav:casino')}</h2>
          <FooterLinkList items={casinoItems} soonLabel={soon} />
        </section>
        <section>
          <h2>{t('nav:sports')}</h2>
          <FooterLinkList items={sportsItems} soonLabel={soon} />
        </section>
        <section>
          <h2>{t('common:infoSection')}</h2>
          <FooterLinkList items={infoItems} soonLabel={soon} />
        </section>
        <section>
          <h2>{t('common:footerSupportSecurity')}</h2>
          <FooterLinkList items={supportItems} soonLabel={soon} />
        </section>
      </div>

      <article className="vbb-rich-footer-support">
        <div className="vbb-rich-footer-support-copy">
          <h2>
            {t('common:footerSupport')}
            <span className="badge-247">24/7</span>
          </h2>
          <p>{t('common:supportInvite')}</p>
        </div>
        <Link to="/support" className="btn btn-primary btn-block">
          {t('common:contactSupport')}
        </Link>
      </article>

      <div className="vbb-rich-footer-pay">
        <Link className="vbb-rich-footer-pay-card" to="/cashier?tab=deposit">
          <strong>{t('common:storyCrypto')}</strong>
          <span className="vbb-rich-footer-pay-icons" aria-hidden="true">
            <span>USDT</span>
            <span>TON</span>
            <span>BTC</span>
          </span>
          <small>{t('common:cryptoMethodsHint')}</small>
        </Link>
        <Link className="vbb-rich-footer-pay-card" to="/cashier?tab=deposit">
          <strong>{t('common:fiatMethods')}</strong>
          <span className="vbb-rich-footer-pay-icons" aria-hidden="true">
            <span>Card</span>
            <span>M10</span>
            <span>Terminal</span>
          </span>
          <small>{t('common:fiatMethodsHint')}</small>
        </Link>
      </div>

      <section className="vbb-rich-footer-social" aria-label={t('common:footerSocial')}>
        <div>
          <h2>{t('common:footerSocial')}</h2>
          <p>{t('common:footerSocialPending')}</p>
        </div>
      </section>

      <div className="vbb-rich-footer-bottom">
        <p className="vbb-rich-footer-copy">{t('common:footerRights', { year: 2026 })}</p>
        <span className="vbb-rich-footer-age" title={t('common:footerAgeHint')}>
          18+
        </span>
        <button type="button" className="vbb-rich-footer-top" onClick={scrollTop} aria-label={t('common:scrollToTop')}>
          <span aria-hidden="true">↑</span>
        </button>
      </div>

    </footer>
  );
}
